import { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';

import { useTerminalConfig } from '../../context/config-context';
import { LayoutContainer } from './LayoutContainer';

interface AgentComponentSpec {
  readonly component: string;
  readonly props?: Record<string, unknown>;
  readonly key?: string;
}

interface AgentSpecShape {
  readonly layout?: 'stack' | 'grid';
  readonly columns?: number;
  readonly children?: readonly AgentComponentSpec[];
}

const Fallback = styled.div`
  font-size: 0.7rem;
  color: var(--text-label);
  font-family: var(--font-mono, monospace);
  padding: 2px 0;
`;

/** Isolates a single injected component so a bad render can't crash the chat. */
class RenderBoundary extends Component<
  {
    readonly component: string;
    readonly children: ReactNode;
    readonly onError?: (component: string, error: unknown) => void;
  },
  { readonly failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Report so silent render failures of host-injected components are visible
    // in the field; the inline fallback below is the user-facing signal.
    this.props.onError?.(this.props.component, error);
    void info;
  }

  render(): ReactNode {
    if (this.state.failed) {
      return <Fallback>[failed to render: {this.props.component}]</Fallback>;
    }
    return this.props.children;
  }
}

/**
 * Renders a generative-UI `AgentSpec` by mounting host-injected components
 * (`config.capabilities.components`) by key. The package owns the layout +
 * key lookup; the host supplies the leaf components. Unknown keys / render
 * errors degrade to an inline note rather than crashing the chat.
 */
export function AgentRenderer({
  spec,
}: {
  readonly spec: unknown;
}): JSX.Element {
  const config = useTerminalConfig();
  const components = config.capabilities?.components ?? {};
  const s = (spec ?? {}) as AgentSpecShape;
  const children = Array.isArray(s.children) ? s.children : [];

  return (
    <LayoutContainer layout={s.layout} columns={s.columns}>
      {children.map((child, i) => {
        const key = child?.key ?? `${child?.component ?? 'item'}-${i}`;
        // The agent emits the catalog's canonical key (e.g. `performance_chart`).
        // Resolve it tolerantly so a host that registers under a namespaced key
        // (e.g. `paradex-performance_chart`) still matches.
        const Comp =
          child != null
            ? (components[child.component] ??
              components[`paradex-${child.component}`])
            : undefined;
        if (Comp == null) {
          return (
            <Fallback key={key}>
              [unknown component: {child?.component ?? '?'}]
            </Fallback>
          );
        }
        return (
          <RenderBoundary
            key={key}
            component={child.component}
            onError={(component, error) =>
              config.logException?.(error, {
                scope: 'generative-ui-render',
                component,
              })
            }
          >
            <Comp {...(child.props ?? {})} />
          </RenderBoundary>
        );
      })}
    </LayoutContainer>
  );
}
